"use client"

import type { ReactNode } from "react"

import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { withAmazonTag, amazonTagFamilyForPlacement, type AmazonTagFamily } from "@/lib/affiliate"
import { contentAnalytics, type AffiliatePlacement, type EventProperties } from "@/lib/analytics"

const ASIN_IN_URL = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:[/?#]|$)/i

interface TrackedAffiliateLinkProps {
  href: string
  /** Placement key: stamped on `data-affiliate-placement`, both click events and the impression. */
  placement: AffiliatePlacement
  productName: string
  /** `article_slug` on every event, e.g. `tool:soil-temperature`. */
  articleSlug: string
  asin?: string | null
  /** 1-based slot within the rendered set. */
  position?: number
  /** Tag family override; defaults to the placement's family (site tag when unmapped). */
  tagFamily?: AmazonTagFamily | null
  /** Extra properties stamped on the impression and both click events. */
  eventProps?: EventProperties
  className?: string
  children: ReactNode
}

/**
 * A single affiliate anchor that owns its telemetry: a per-product
 * `affiliate_product_shown` impression when half visible, and the
 * `affiliate_link_clicked` + `affiliate_product_clicked` pair on click, all on
 * the same placement so CTR joins. Use it for standalone text links that are
 * not part of a picks card.
 */
export function TrackedAffiliateLink({
  href: rawHref,
  placement,
  productName,
  articleSlug,
  asin: asinProp,
  position,
  tagFamily,
  eventProps,
  className,
  children,
}: TrackedAffiliateLinkProps) {
  const family = tagFamily === undefined ? amazonTagFamilyForPlacement(placement) : tagFamily
  const href = withAmazonTag(rawHref, family)
  const asin = asinProp ?? href.match(ASIN_IN_URL)?.[1]?.toUpperCase()
  const shared: EventProperties = {
    ...eventProps,
    ...(position != null ? { position } : {}),
  }

  const impressionRef = useAffiliateImpression(placement, {
    ...shared,
    article_slug: articleSlug,
    product_name: productName,
    affiliate_link: href,
    asin,
    placement,
    denominator: "product",
  })

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(articleSlug, productName, href, placement, asin, shared)
    contentAnalytics.affiliateProductClicked(placement, {
      ...shared,
      productName,
      affiliateLink: href,
      asin,
      article_slug: articleSlug,
      placement,
    })
  }

  return (
    <a
      ref={impressionRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      data-affiliate-placement={placement}
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  )
}
